import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Center,
  Circle,
  Divider,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useMediaQuery,
  useToast,
  useToken,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useQueryClient } from '@tanstack/react-query';
import Color from 'color';
import { useTranslation } from 'react-i18next';
import { MdNavigateNext } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppContext } from '@/app/Providers';
import {
  IconAiStars,
  IconAlertCircle,
  IconClock,
  IconEllipseShade,
  IconRepeat,
  IconRightArrow,
  LoaderFull,
  SlideIn,
} from '@/components';
import { LoaderAnimated } from '@/components/LoaderAnimated';
import { FullScreenModal } from '@/components/Screen/FullScreen';
import { VideoPlayer } from '@/components/VideoPlayer';
import { VideoRecorder } from '@/components/VideoRecorder';
import {
  interviewQueryKeys,
  useAnswerQuestionMutation,
  useGetCandidateInterviewQuestion,
  useGetCandidateQuery,
  useMediaAccessMutation,
} from '@/features/interview/useInterviewQueries';
import { useMediaDeviceContext } from '@/features/media';
import {
  type UseVideoRecorderReturn,
  useVideoRecorder,
} from '@/features/video-recorder/useVideoRecorder';
import { QuestionType } from '@/services/api';
import { useEffectOnce } from '@/utils/hooks';
import { getFullname } from '@/utils/identity';
import { uploadObjectToS3 } from '@/utils/s3';
import { sleep } from '@/utils/sleep';

import { useCoachInterviewInfoQuery } from '../coach-onboarding/useOnboardingCoachQueries';
import {
  CoachLayoutPage,
  CoachLayoutPageContent,
} from '../coach/CoachLayoutPage';
import { COACH_PATH } from '../coach/constants';
import { useCanProceedInterviewStep } from '../interview/useCanProceedInterviewStep';
import { CoachInterviewNav } from './CoachInterviewNav';
import { COACH_INTERVIEW_PATH } from './constants';
import { useCoachCredential } from './useCoachCredential';

const PREPARE_TIME = 5;

export function PageCoachInterviewQuestions() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { candidateId, token, interviewId } = useCoachCredential();
  const [answerBeingSubmitted, setAnswerBeingSubmitted] = useState(false);
  const technicalProblemDisclosure = useDisclosure();

  const { t } = useTranslation(['interview', 'common', 'coach']);
  const toast = useToast();
  const duration = useRef<number | null>(null);
  const navigate = useNavigate();

  const { setIsShowBanner } = useAppContext();
  useEffectOnce(() => {
    setIsShowBanner(false);
  });

  const coachInterviewInfoQuery = useCoachInterviewInfoQuery({
    interviewsId: interviewId!,
    securityToken: token!,
  });

  useEffect(() => {
    if (
      !coachInterviewInfoQuery.isLoading &&
      coachInterviewInfoQuery.data &&
      !coachInterviewInfoQuery.data.isPaid
    ) {
      navigate(`${COACH_PATH}/new/overview?${searchParams.toString()}`);
    }
  }, [
    coachInterviewInfoQuery.isLoading,
    coachInterviewInfoQuery.data,
    navigate,
    searchParams,
  ]);

  const candidateQuery = useGetCandidateQuery(
    {
      candidateId: candidateId!,
      token: token!,
    },
    {
      enabled: !!candidateId && !!token,
    }
  );

  const fullName = getFullname(
    candidateQuery.data?.name,
    candidateQuery.data?.surname
  );

  const questionQuery = useGetCandidateInterviewQuestion(
    {
      candidateId: candidateId!,
      token: token!,
    },
    {
      enabled: !!candidateId && !!token,
    }
  );

  const { canProceedInterviewStep } = useCanProceedInterviewStep({
    interviewHasVideo: candidateQuery?.data?.hasVideo ?? false,
    currentQuestionTypeIsVideo:
      questionQuery?.data?.question?.questionType === QuestionType.Video,
  });

  const answerMutation = useAnswerQuestionMutation(token!);
  const mediaAccessMutation = useMediaAccessMutation(token!);

  const videoRecorder = useVideoRecorder({
    durationSec: questionQuery.data?.question?.durationMin
      ? questionQuery.data?.question?.durationMin * 60
      : 0,
    preparationSec: PREPARE_TIME,
  });

  useEffect(() => {
    if (
      questionQuery.data?.question?.durationMin &&
      duration.current === null
    ) {
      duration.current = questionQuery.data?.question?.durationMin;
      videoRecorder.resetCountdown();
    }
  }, [
    questionQuery.data?.question?.durationMin,
    questionQuery.isLoading,
    videoRecorder,
  ]);

  const submitAnswer = async (video: Blob) => {
    if (
      !candidateId ||
      !questionQuery.data?.question?.id ||
      !candidateQuery.data?.interviewId
    ) {
      return;
    }

    const access = await mediaAccessMutation.mutateAsync({
      candidateId: candidateId,
      contentType: video.type,
      interviewId: candidateQuery.data?.interviewId,
      questionId: questionQuery.data?.question?.id,
    });

    await uploadObjectToS3({
      blob: video,
      uploadUrl: access.signedUrl!,
      headers: {
        ...access.requestHeaders,
        'content-type': video.type,
      },
    });

    await answerMutation.mutateAsync({
      candidateId: candidateId,
      questionId: questionQuery.data?.question?.id,
      body: {
        videoUrl: access.finalObjectUrl,
      },
    });
  };

  const handleSubmit = async (video: Blob) => {
    const submitAnswerTask = submitAnswer(video);

    toast.promise(submitAnswerTask, {
      success: { title: t('interview:uploadSuccess') },
      error: { title: t('interview:uploadError') },
      loading: { title: t('interview:uploadingVideo') },
    });

    setAnswerBeingSubmitted(true);
    await submitAnswerTask;

    if (
      questionQuery.data?.remainingQuestions &&
      questionQuery.data?.remainingQuestions <= 1
    ) {
      navigate(
        COACH_INTERVIEW_PATH + '/result-status?' + searchParams.toString()
      );
      setAnswerBeingSubmitted(false);
    } else {
      await queryClient.invalidateQueries({
        queryKey: interviewQueryKeys.getAllCandidatesQuestions(),
      });

      duration.current = null;
      videoRecorder.handleTryAgain();
      setAnswerBeingSubmitted(false);
    }
  };

  useEffect(() => {
    if (
      questionQuery.data?.remainingQuestions === 0 ||
      questionQuery.data?.question === null
    ) {
      navigate(
        COACH_INTERVIEW_PATH + '/result-status?' + searchParams.toString()
      );
    }
  }, [
    navigate,
    questionQuery.data?.question,
    questionQuery.data?.remainingQuestions,
    searchParams,
  ]);

  const { selectedAudioDeviceId, selectedVideoDeviceId } =
    useMediaDeviceContext();

  const [isSmallScreen] = useMediaQuery('(max-width: 500px)');

  let videoConstraints: MediaTrackConstraints = {
    deviceId: selectedVideoDeviceId,
  };

  if (isSmallScreen) {
    videoConstraints = {
      ...videoConstraints,
      aspectRatio: 3 / 2,
    };
  }

  if (questionQuery.isLoading || coachInterviewInfoQuery.isLoading) {
    return <LoaderFull />;
  }

  // Don't render anything if payment check hasn't completed or user is not paid
  if (!coachInterviewInfoQuery.data?.isPaid) {
    return <LoaderFull />;
  }

  return (
    <CoachLayoutPage
      pt={{ md: 8 }}
      px={{ md: 6 }}
      showFooter={false}
      containerMaxWidth="container.xl"
      mx="auto"
      nav={
        <Stack display={{ base: 'none', lg: 'block' }}>
          <CoachInterviewNav
            subStepIndex={1}
            currentQuestionIndex={(questionQuery.data?.question?.rank ?? 0) - 1}
            questionCount={candidateQuery?.data?.interview?.questionCount}
          />
        </Stack>
      }
    >
      <CoachLayoutPageContent mt={{ base: 10, md: 0 }}>
        <Stack
          gap={0}
          bg={{ md: 'white' }}
          borderRadius="xl"
          maxW={812}
          w={{ base: '100%', xl: 812 }}
          borderColor="gray.50"
          pos="relative"
        >
          <Stack
            mt={{ base: -4, lg: 0 }}
            minH={{ base: '50vh', xl: 'auto' }}
            spacing={5}
            p={{ md: '6' }}
          >
            <HStack align="end">
              <Circle size="48px" bg="gray.50" mb="26px">
                <IconAiStars fontSize="24px" />
              </Circle>

              <VStack align="start">
                <VStack
                  bg="gray.50"
                  align="start"
                  py={3}
                  px={5}
                  borderRadius="32px 32px 32px 4px"
                  gap={1}
                  border="1px solid"
                  borderColor="gray.100"
                  w="calc(100% - 56px)"
                  mr="auto"
                >
                  <Text color="gray.500" fontSize={14}>
                    {t('common:detail.question')} #
                    {questionQuery.data?.question?.rank ?? 0}
                  </Text>

                  <Text
                    fontSize={{ base: '14', md: '18' }}
                    fontWeight="medium"
                    userSelect="none"
                  >
                    {questionQuery.data?.question?.question}
                  </Text>
                </VStack>

                <Text color="gray.300" fontWeight="medium" fontSize="xs" pl={1}>
                  {t('coach:new.overview.interviewName')}
                </Text>
              </VStack>
            </HStack>

            {answerBeingSubmitted ? (
              <LoaderAnimated />
            ) : (
              <HStack flexDir="row-reverse" align="end">
                <Circle
                  size="48px"
                  bg="brand.50"
                  color="brand.500"
                  fontWeight="bold"
                  fontSize="2xl"
                  mb="26px"
                >
                  {fullName[0]?.toAppLocaleUpperCase()}
                </Circle>

                <VStack flexGrow={1} align="end">
                  <Flex
                    pos="relative"
                    overflow="hidden"
                    bg="gray.700"
                    rounded="xl"
                    w="full"
                    maxW={{ base: 'calc(100% - 56px)', xl: '652' }}
                    h={{ md: '40vh' }}
                    maxH="400"
                    ml="auto"
                    borderRadius="32px 32px 4px 32px"
                    border="4px solid"
                    borderColor="brand.500"
                    boxShadow="0px 0px 32px 0px #CFBAE999"
                  >
                    <Loading {...videoRecorder} />

                    <VideoRecorder
                      ref={videoRecorder.webcamRef}
                      display={
                        !videoRecorder.isVideoPlayMode ? undefined : 'none'
                      }
                      audioConstraints={{
                        deviceId: selectedAudioDeviceId,
                      }}
                      videoConstraints={videoConstraints}
                      ratio={{ base: 2 / 3, sm: 7 / 4 }}
                      rounded="none"
                    />

                    <VideoPlayer
                      ref={videoRecorder.videoRef}
                      display={
                        videoRecorder.isVideoPlayMode ? undefined : 'none'
                      }
                    />
                  </Flex>

                  <Text
                    color="gray.300"
                    fontSize="xs"
                    fontWeight="medium"
                    pr={1}
                  >
                    {fullName.toAppLocaleUpperCase()}
                  </Text>
                </VStack>
              </HStack>
            )}
          </Stack>

          <Divider />

          <ControlButtons
            {...videoRecorder}
            handleSubmit={async () => {
              if (videoRecorder.capturing) {
                videoRecorder.handleStopCapture();
                await sleep(500);
              }

              if (videoRecorder.hasTechnicalIssue) {
                return technicalProblemDisclosure.onOpen();
              }
              videoRecorder.onSubmit(handleSubmit);
            }}
            durationSec={(questionQuery.data?.question?.durationMin ?? 1) * 60}
            remainingQuestions={questionQuery.data?.remainingQuestions ?? 0}
            answerBeingSubmitted={answerBeingSubmitted}
            currentQuestionRank={questionQuery.data?.question?.rank ?? 0}
            canProceedInterviewStep={canProceedInterviewStep}
          />
        </Stack>

        <TechnicalProblemModal
          handleRetry={() => {
            videoRecorder.handleTryAgain();
            technicalProblemDisclosure.onClose();
          }}
          handleSubmit={async () => {
            technicalProblemDisclosure.onClose();
            videoRecorder.onSubmit(handleSubmit);
          }}
          {...technicalProblemDisclosure}
        />
        <FullScreenModal />
      </CoachLayoutPageContent>
    </CoachLayoutPage>
  );
}

interface ControlButtonsProps extends UseVideoRecorderReturn {
  durationSec: number;
  remainingQuestions: number;
  handleSubmit: () => void;
  answerBeingSubmitted: boolean;
  currentQuestionRank: number;
  canProceedInterviewStep: boolean;
}

const ControlButtons: React.FC<ControlButtonsProps> = ({
  capturing,
  durationSec,
  hasVideo,
  handleSubmit,
  count,
  remainingQuestions,
  answerBeingSubmitted,
  canProceedInterviewStep,
}) => {
  const formattedCount = new Date(count * 1000).toISOString().slice(14, 19);
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);
  const { t } = useTranslation(['interview', 'common']);

  const stage = useMemo(() => {
    if (!capturing && hasVideo) return 'ready-to-upload';
    if (capturing) return 'recording';
    return 'ready-to-record';
  }, [capturing, hasVideo]);

  useEffect(() => {
    if (answerBeingSubmitted || !canProceedInterviewStep) return;

    if (!!count && count < durationSec) {
      setIsBtnDisabled(false);
    }
  }, [count, durationSec, answerBeingSubmitted, canProceedInterviewStep]);

  const handleFinishQuestion = () => {
    setIsBtnDisabled(true);
    handleSubmit();
  };

  return (
    <HStack
      zIndex="modal"
      bottom={0}
      left={0}
      right={0}
      borderTopWidth={{ base: 1, md: 0 }}
      borderColor="gray.50"
      px={{ base: 4, md: 6 }}
      py={{ base: 3, md: 4 }}
      width="full"
      position={{ base: 'fixed', md: 'relative' }}
      bg={{ base: 'white', md: 'none' }}
      justify="space-between"
      gap={6}
    >
      <Flex
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: '0.5', md: '2' }}
        align={{ md: 'center' }}
      >
        {(stage === 'recording' || stage === 'ready-to-record') && (
          <>
            <IconClock
              fontSize="24px"
              display={{ base: 'none', md: 'block' }}
            />
            <Text fontSize={{ base: '14', md: 'md' }} color="gray.500">
              {t('interview:remainingTime')}:
            </Text>

            <Text color="primary.500" fontWeight="semibold" fontSize="lg">
              {formattedCount}
            </Text>
          </>
        )}
      </Flex>

      <Flex gap="2">
        <Button
          onClick={handleFinishQuestion}
          isDisabled={isBtnDisabled}
          size="md"
          variant="@primary"
          rightIcon={<MdNavigateNext />}
        >
          {remainingQuestions > 1
            ? t('common:nextQuestion')
            : t('interview:finishInterview')}
        </Button>
      </Flex>
    </HStack>
  );
};

const flash = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

const Loading = ({ initialCount }: UseVideoRecorderReturn) => {
  const [gray700] = useToken('colors', ['gray.700']);
  const { t } = useTranslation(['interview']);

  return (
    <Center
      position="absolute"
      display={initialCount >= 0 ? undefined : 'none'}
      zIndex="overlay"
      left={0}
      right={0}
      top={0}
      border={0}
      flex="1"
      bg={Color(gray700).alpha(0.6).string()}
      h="full"
      flexDir="column"
    >
      <Text
        fontSize="small"
        color="white"
        sx={{
          animation: `${flash} 1s infinite`,
        }}
      >
        {t('interview:recordingWillStartIn', {
          time: initialCount,
        })}
      </Text>
    </Center>
  );
};

interface TechnicalProblemModalProps extends Omit<ModalProps, 'children'> {
  handleSubmit: () => void;
  handleRetry: () => void;
}

export const TechnicalProblemModal: React.FC<TechnicalProblemModalProps> = ({
  isOpen,
  onClose,
  handleSubmit,
  handleRetry,
}) => {
  const { t } = useTranslation(['common', 'coach', 'candidate', 'interview']);
  return (
    <Modal isOpen={isOpen} isCentered onClose={onClose}>
      <ModalOverlay />

      <ModalContent
        mx={{ base: 2, sm: 0 }}
        w="420px"
        borderRadius="xl"
        pt={6}
        alignItems="center"
      >
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />
        <ModalHeader py={2} justifyItems="center">
          <Stack px={10}>
            <Flex justifyContent="center" position="relative" mt={3}>
              <IconEllipseShade fontSize={64} position="absolute" top={-6} />
              <Flex
                h={12}
                w={12}
                borderWidth="1px"
                borderColor="#EFD3B8"
                borderRadius="50%"
                justifyContent="center"
                alignItems="center"
              >
                <IconAlertCircle color="#CA7219" fontSize={28} />
              </Flex>
            </Flex>
          </Stack>
        </ModalHeader>
        <ModalBody w="full" pb={2}>
          <SlideIn>
            <Stack
              alignItems="center"
              borderColor="gray.50"
              px={{ base: 2, sm: 2 }}
              pb={6}
            >
              <Text
                w="full"
                textAlign="center"
                fontSize="24px"
                fontWeight={600}
                mt={2}
              >
                {t('coach:technicalProblem.title')}
              </Text>
              <Text mb={3} textAlign="center" color="gray.600">
                {t('coach:technicalProblem.description')}
              </Text>
              <HStack
                w="full"
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <Button
                  leftIcon={<IconRepeat fontSize={20} />}
                  flex={1}
                  onClick={handleRetry}
                  variant="outline"
                  color="primary.500!"
                >
                  {t('interview:introductionVideo.tryAgain')}
                </Button>
                <Button
                  flex={1}
                  w="full"
                  variant="@primary"
                  rightIcon={<IconRightArrow />}
                  onClick={handleSubmit}
                >
                  {t('common:actions.continue')}
                </Button>
              </HStack>
            </Stack>
          </SlideIn>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
